import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { TrendingDown, Calculator } from 'lucide-react';
import { fmtMoney } from '../utils/locale';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Two-in-one badge cluster that lives directly under the headline price:
 *   • "Price reduced 2× · -€20k" — red, if /price-history has any drops in 90d
 *   • "True 30-yr cost: €877k" — purple, the Honest Number TCO summary
 *
 * Both fetch in parallel, fail silently (badges just don't render), and
 * stay invisible on rental listings (TCO doesn't apply).
 *
 * Backend: GET /api/properties/{id}/price-history + /honest-number
 * Runbook references: 3.7 (Honest Number) + 3.10 (Auction Whisperer).
 */
const HonestNumberPriceDropBadges = ({ propertyId, propertyType, country }) => {
  const [honest, setHonest] = useState(null);
  const [history, setHistory] = useState(null);

  useEffect(() => {
    if (!propertyId) return undefined;
    let cancelled = false;
    Promise.all([
      axios.get(`${BACKEND_URL}/api/properties/${propertyId}/honest-number`).catch(() => null),
      axios.get(`${BACKEND_URL}/api/properties/${propertyId}/price-history?days=90`).catch(() => null),
    ]).then(([h, ph]) => {
      if (cancelled) return;
      if (h?.data) setHonest(h.data);
      if (ph?.data) setHistory(ph.data);
    });
    return () => { cancelled = true; };
  }, [propertyId]);

  const isRent = (propertyType || '').toLowerCase() === 'rent';
  const badge = history?.badge;
  const total = !isRent && honest?.total ? honest.total : null;
  const totalFmt = total ? fmtMoney(total, country) : null;

  if (!badge && !totalFmt) return null;

  return (
    <div className="flex flex-wrap items-center gap-2 mt-2"
         data-testid="honest-number-price-drop-badges">
      {badge && (
        <span
          className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full bg-red-50 text-red-800 border border-red-100"
          data-testid="price-drop-badge"
          title="Price has dropped from its earlier asking — buyers love seeing this"
        >
          <TrendingDown className="h-3 w-3" />
          {badge.label}
        </span>
      )}
      {totalFmt && (
        <span
          className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full bg-purple-50 text-purple-800 border border-purple-100"
          data-testid="honest-number-badge"
          title="The Honest Number: 30-year true cost (price + stamp + solicitor + mortgage interest)"
        >
          <Calculator className="h-3 w-3" />
          True 30-yr cost: {totalFmt}
        </span>
      )}
    </div>
  );
};

export default HonestNumberPriceDropBadges;
